import { Calendar, Clock, MapPin, ArrowRight } from "lucide-react";
import { useContent, resolveImage } from "../contexts/ContentContext";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};

const fmtDateShort = (iso) => {
  if (!iso) return { month: "", day: "" };
  const d = new Date(iso + "T12:00:00");
  if (Number.isNaN(d.getTime())) return { month: "", day: "" };
  return {
    month: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
    day: String(d.getDate()),
  };
};

const isUpcoming = (iso) => {
  if (!iso) return false;
  const d = new Date(iso + "T23:59:59");
  if (Number.isNaN(d.getTime())) return true; // unparsable → keep visible
  // Compare against start of today (server-local). Past dates auto-hide.
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return d >= todayStart;
};

export const Events = () => {
  const { data: events } = useContent("events");
  if (!events) return null;

  const items = (events.items || []).filter((e) => isUpcoming(e.date));
  // Sort soonest first
  items.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  if (items.length === 0) return null;

  return (
    <section
      id="events"
      data-testid="events-section"
      className="py-20 md:py-28 bg-white relative overflow-hidden"
    >
      <div aria-hidden="true" className="absolute -top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#2563EB]/10 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#FFD100]/15 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <span className="section-eyebrow">
              <Calendar className="w-3.5 h-3.5" />
              {events.eyebrow}
            </span>
            <h2
              data-testid="events-heading"
              className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 leading-tight"
            >
              {events.heading}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-slate-600 max-w-lg leading-relaxed">{events.intro}</p>
          </div>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((e, i) => {
            const short = fmtDateShort(e.date);
            const photo = resolveImage(e.image);
            return (
              <article
                key={i}
                data-testid={`event-card-${i}`}
                className="card-soft overflow-hidden flex flex-col group hover:-translate-y-1 transition-transform"
              >
                <div className="relative aspect-[16/10] bg-gradient-to-br from-[#1E3A8A] via-[#1D4ED8] to-[#2563EB]">
                  {photo ? (
                    <img
                      src={photo}
                      alt={e.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center">
                      <Calendar className="w-14 h-14 text-white/30" strokeWidth={1.5} />
                    </div>
                  )}
                  {/* Date stamp */}
                  {short.day && (
                    <div className="absolute top-4 left-4 bg-white rounded-2xl px-3.5 py-2 shadow-[0_8px_30px_rgba(15,23,42,0.18)] ring-1 ring-white/40 text-center">
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1D4ED8]">{short.month}</p>
                      <p className="font-display text-2xl font-extrabold text-slate-900 leading-none mt-0.5">{short.day}</p>
                    </div>
                  )}
                </div>

                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <h3 className="font-display text-xl md:text-2xl font-bold text-slate-900 leading-tight">{e.title}</h3>

                  <div className="mt-4 space-y-2 text-sm text-slate-600">
                    {e.date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#2563EB] shrink-0" />
                        <span>{fmtDate(e.date)}</span>
                      </div>
                    )}
                    {e.time && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#2563EB] shrink-0" />
                        <span>{e.time}</span>
                      </div>
                    )}
                    {e.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#2563EB] shrink-0" />
                        <span>{e.location}</span>
                      </div>
                    )}
                  </div>

                  {e.description && (
                    <p className="mt-4 text-sm text-slate-600 leading-relaxed flex-1">{e.description}</p>
                  )}

                  <a
                    href="#get-involved"
                    data-testid={`event-cta-${i}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] group-hover:gap-2.5 transition-all self-start"
                  >
                    Get details <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
